// CreatePOModal.jsx
// Modal for creating a new purchase order

import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import VendorAPI from '../../../services/vendorService';
import PurchasingAPI from '../../../services/purchasingService';
import { isMultiLocation, getLocationName } from '../../../util/locationUtils';

class CreatePOModal extends Component {
    state = {
        vendors: [],
        form: {
            vendor_id: '',
            location_id: this.props.selectedLocationId || '',
            expected_date: '',
            notes: ''
        },
        saving: false,
        loading: true,
        error: null
    };

    async componentDidMount() {
        try {
            const vendors = await VendorAPI.getVendors(this.props.installerId);
            const activeVendors = (Array.isArray(vendors) ? vendors : [])
                .map(v => ({ ...v, vendor_id: v.vendor_id || v.id }))
                .filter(v => v.is_active !== false)
                .sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''));
            this.setState({ vendors: activeVendors, loading: false });
        } catch (err) {
            console.error('Error loading vendors:', err);
            this.setState({ error: 'Failed to load vendors.', loading: false });
        }
    }

    handleChange = (field, value) => {
        this.setState(prev => {
            const form = { ...prev.form, [field]: value };
            // Auto-fill expected date based on vendor lead time
            if (field === 'vendor_id' && value) {
                const vendor = prev.vendors.find(v => String(v.vendor_id) === String(value));
                if (vendor && vendor.lead_time_days) {
                    const date = new Date();
                    date.setDate(date.getDate() + vendor.lead_time_days);
                    form.expected_date = date.toISOString().split('T')[0];
                }
            }
            return { form };
        });
    };

    handleCreate = async () => {
        const { form } = this.state;
        if (!form.vendor_id) {
            this.setState({ error: 'Please select a vendor.' });
            return;
        }
        this.setState({ saving: true, error: null });
        try {
            const vendorId = parseInt(form.vendor_id, 10);
            const locationId = form.location_id ? parseInt(form.location_id, 10) : null;
            const result = await PurchasingAPI.createPurchaseOrder({
                installer_id: this.props.installerId,
                vendor_id: vendorId,
                location_id: !isNaN(locationId) ? locationId : null,
                expected_date: form.expected_date || null,
                notes: form.notes || null
            });
            const poId = result.purchase_order_id || result.id || (result.data && (result.data.purchase_order_id || result.data.id));
            this.props.onCreated(poId);
        } catch (err) {
            console.error('Error creating PO:', err);
            const serverMsg = err?.response?.data?.error || err?.response?.data?.message || '';
            this.setState({
                saving: false,
                error: serverMsg ? `Failed to create purchase order: ${serverMsg}` : 'Failed to create purchase order.'
            });
        }
    };

    render() {
        const { show, onHide, locations } = this.props;
        const { vendors, form, saving, loading, error } = this.state;
        const multiLoc = isMultiLocation(locations);

        return (
            <Modal show={show} onHide={onHide} centered backdrop={saving ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-sharp fa-regular fa-file-plus mr-2"></i>
                        Create Purchase Order
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <div className="alert alert-danger">{error}</div>}

                    {loading ? (
                        <div className="text-center py-4">
                            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                        </div>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Vendor <span className="text-danger">*</span></Form.Label>
                                <Form.Control as="select"
                                    value={form.vendor_id}
                                    onChange={(e) => this.handleChange('vendor_id', e.target.value)}>
                                    <option value="">Select a vendor...</option>
                                    {vendors.map(v => (
                                        <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            {multiLoc && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Location</Form.Label>
                                    <Form.Control as="select"
                                        value={form.location_id}
                                        onChange={(e) => this.handleChange('location_id', e.target.value)}>
                                        <option value="">All Locations</option>
                                        {(locations || []).map(loc => (
                                            <option key={loc.location_id} value={loc.location_id}>
                                                {getLocationName(loc)}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Expected Delivery Date</Form.Label>
                                <Form.Control type="date"
                                    value={form.expected_date}
                                    onChange={(e) => this.handleChange('expected_date', e.target.value)} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control as="textarea" rows={2}
                                    value={form.notes}
                                    onChange={(e) => this.handleChange('notes', e.target.value)} />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button
                        style={{ backgroundColor: '#3FB2BE', borderColor: '#3FB2BE' }}
                        onClick={this.handleCreate}
                        disabled={saving || loading || !form.vendor_id}
                    >
                        {saving ? (
                            <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Creating...</>
                        ) : (
                            'Create & Add Lines'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default CreatePOModal;
